/**
 * Subscription tiers, credit allotments and abuse guardrails.
 *
 * Every limit in the product is named here. Nothing downstream should hardcode a
 * number inline — change a plan by editing this file and the Stripe Price, and
 * the API, UI copy and ledger enforcement all follow.
 */
export const PLAN_TIERS = ['free', 'standard', 'advanced'];
/**
 * Length of the free diagnostic.
 *
 * Five questions per content domain on both blueprints, which is the fewest
 * that still produces a per-domain figure worth showing on the radar, and short
 * enough to finish in one sitting on a phone. The full-length papers are 70 and
 * 120 questions; asking for two uninterrupted hours before a candidate has any
 * reason to trust the product is why a free full paper converts badly whatever
 * it costs to serve.
 */
export const DIAGNOSTIC_QUESTION_COUNT = 25;
/** Free diagnostics per exam, ever — not per period. The hook fires once. */
export const FREE_DIAGNOSTICS_PER_EXAM = 1;
/** Practice questions a free account can be served per rolling 24 hours. */
export const FREE_PRACTICE_QUESTIONS_PER_DAY = 20;
/** Actions that consume credits. Practice question serving is deliberately absent. */
export const CREDIT_ACTIONS = ['feedback_report', 'revision_plan', 'tutor_message'];
/**
 * Billing cadence.
 *
 * Preparation for these exams is inherently time-boxed — the revision planner
 * defaults to an eight-week runway — so an open-ended monthly subscription is a
 * poor fit for the way the product is actually bought. The quarterly bundle
 * covers a whole preparation cycle in one decision.
 */
export const BILLING_INTERVALS = ['month', 'quarter'];
/** Months billed per interval, used to derive the effective monthly rate. */
export const INTERVAL_MONTHS = {
    month: 1,
    quarter: 3,
};
export const PLAN_LIMITS = {
    free: {
        label: 'Free',
        priceUsdCents: { month: 0, quarter: 0 },
        reports: 0,
        revisions: 0,
        tutorMessages: 0,
        fullLengthSims: 0,
        practiceQuestionsPerDay: FREE_PRACTICE_QUESTIONS_PER_DAY,
        model: 'STANDARD_MODEL',
        features: [
            `A ${DIAGNOSTIC_QUESTION_COUNT}-question diagnostic for each exam, weighted like the real paper`,
            'Your weighted score, domain-by-domain breakdown and pace read',
            'Worked explanations for every diagnostic question',
            `${FREE_PRACTICE_QUESTIONS_PER_DAY} practice questions a day`,
        ],
    },
    standard: {
        label: 'Standard',
        priceUsdCents: { month: 1000, quarter: 2500 },
        reports: 4,
        revisions: 2,
        tutorMessages: 40,
        fullLengthSims: Number.POSITIVE_INFINITY,
        practiceQuestionsPerDay: Number.POSITIVE_INFINITY,
        model: 'STANDARD_MODEL',
        features: [
            'Everything in Free',
            'Unlimited full-length, exam-accurate timed simulations',
            'Unlimited practice questions',
            'Progress tracking and benchmark history over time',
            '4 AI feedback reports per month',
            '2 AI revision timetables per month',
            '40 AI tutor messages per month',
        ],
    },
    advanced: {
        label: 'Advanced',
        priceUsdCents: { month: 2000, quarter: 5000 },
        reports: Number.POSITIVE_INFINITY,
        revisions: Number.POSITIVE_INFINITY,
        tutorMessages: 200,
        fullLengthSims: Number.POSITIVE_INFINITY,
        practiceQuestionsPerDay: Number.POSITIVE_INFINITY,
        model: 'ADVANCED_MODEL',
        features: [
            'Everything in Standard',
            'Unlimited AI feedback reports',
            'Unlimited AI revision timetables',
            '200 AI tutor messages per month',
            'Reports and tutoring generated by our most capable model',
        ],
    },
};
/** Effective monthly rate for an interval, for "works out at $X/month" copy. */
export function monthlyEquivalentCents(tier, interval) {
    return Math.round(PLAN_LIMITS[tier].priceUsdCents[interval] / INTERVAL_MONTHS[interval]);
}
/** Whole percent saved by buying the interval rather than paying monthly. */
export function intervalSavingPct(tier, interval) {
    const monthly = PLAN_LIMITS[tier].priceUsdCents.month;
    if (monthly <= 0 || interval === 'month')
        return 0;
    const full = monthly * INTERVAL_MONTHS[interval];
    const actual = PLAN_LIMITS[tier].priceUsdCents[interval];
    return Math.round(((full - actual) / full) * 100);
}
/** True when the plan includes full-length simulations at all. */
export function includesFullLengthSims(tier) {
    return PLAN_LIMITS[tier].fullLengthSims > 0;
}
/** Maps a credit action to the plan field that funds it. */
export const CREDIT_ACTION_FIELD = {
    feedback_report: 'reports',
    revision_plan: 'revisions',
    tutor_message: 'tutorMessages',
};
/**
 * Hidden per-user daily soft caps.
 *
 * These are a cost safety net against a scripted abuser or a runaway client-side
 * bug — NOT a plan feature. They are never surfaced in marketing or pricing copy,
 * and hitting one logs/alerts rather than failing silently.
 */
export const DAILY_SOFT_CAPS = {
    feedback_report: 10,
    revision_plan: 5,
    tutor_message: 50,
};
export function planLimitFor(tier, action) {
    return PLAN_LIMITS[tier][CREDIT_ACTION_FIELD[action]];
}
export function isUnlimited(tier, action) {
    return planLimitFor(tier, action) === Number.POSITIVE_INFINITY;
}
/** Human-readable action names for ledger rows and quota messages. */
export const CREDIT_ACTION_LABELS = {
    feedback_report: 'AI feedback report',
    revision_plan: 'AI revision timetable',
    tutor_message: 'AI tutor message',
};
export const SUBSCRIPTION_STATUSES = [
    'active',
    'trialing',
    'past_due',
    'canceled',
    'incomplete',
    'incomplete_expired',
    'unpaid',
    'paused',
];
/** Statuses that entitle a user to their paid tier's allotments. */
export const ENTITLING_STATUSES = ['active', 'trialing'];
//# sourceMappingURL=plans.js.map